import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Eye, EyeOff, User, Mail, Lock, Phone, UserCheck, AlertCircle, CheckCircle } from 'lucide-react';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabase = createClient(supabaseUrl, supabaseKey);

interface AfiliadoInfo {
    id: string;
    codigo_afiliado: string;
    corretor_id: string;
    corretor_nome: string;
    corretor_email: string;
}

const Cadastro = () => {
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        senha: '',
        confirmarSenha: '',
        empresa: '',
        cargo: ''
    });

    const [afiliadoInfo, setAfiliadoInfo] = useState<AfiliadoInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [emailEmUso, setEmailEmUso] = useState(false);
    const [verificandoEmail, setVerificandoEmail] = useState(false);

    // Detectar código de afiliado da URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const ref = urlParams.get('ref');

        if (ref) {
            loadAfiliadoInfo(ref);
        }
    }, []);

    const loadAfiliadoInfo = async (codigo: string) => {
        try {
            const { data: afiliado, error } = await supabase
                .from('afiliados')
                .select(`
          id,
          codigo_afiliado,
          corretor_id,
          corretores!inner(
            nome,
            email
          )
        `)
                .eq('codigo_afiliado', codigo)
                .eq('status', 'ativo')
                .single();

            if (error || !afiliado) {
                return;
            }

            setAfiliadoInfo({
                id: afiliado.id,
                codigo_afiliado: afiliado.codigo_afiliado,
                corretor_id: afiliado.corretor_id,
                corretor_nome: (afiliado.corretores as unknown as { nome: string; email: string }).nome,
                corretor_email: (afiliado.corretores as unknown as { nome: string; email: string }).email
            });
        } catch (err) {
            console.error('Erro ao carregar informações do afiliado:', err);
        }
    };

    const formatarTelefone = (valor: string): string => {
        // Remove tudo que não é número
        const numeros = valor.replace(/\D/g, '');
        
        // Aplica a máscara (85)9.9999.9999
        if (numeros.length <= 2) {
            return numeros;
        } else if (numeros.length <= 3) {
            return `(${numeros.slice(0, 2)})${numeros.slice(2)}`;
        } else if (numeros.length <= 7) {
            return `(${numeros.slice(0, 2)})${numeros.slice(2, 3)}.${numeros.slice(3)}`;
        } else if (numeros.length <= 11) {
            return `(${numeros.slice(0, 2)})${numeros.slice(2, 3)}.${numeros.slice(3, 7)}.${numeros.slice(7, 11)}`;
        }
        
        // Limita a 11 dígitos
        return `(${numeros.slice(0, 2)})${numeros.slice(2, 3)}.${numeros.slice(3, 7)}.${numeros.slice(7, 11)}`;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        // Aplica formatação apenas no campo telefone
        const valorFinal = name === 'telefone' ? formatarTelefone(value) : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: valorFinal
        }));
        if (name === 'email') {
            setEmailEmUso(false);
        }
    };

    const verificarEmailJaCadastrado = async (email: string): Promise<boolean> => {
        const emailNormalizado = email.trim().toLowerCase();
        if (!emailNormalizado) return false;

        try {
            setVerificandoEmail(true);
            
            const { data, error } = await supabase
                .from('corretores')
                .select('id, user_id, email')
                .ilike('email', emailNormalizado)
                .limit(1);

            if (error) {
                console.warn('Erro ao verificar email:', error);
                return true;
            }

            return data && data.length > 0;
        } catch (err) {
            console.error('Falha ao verificar e-mail:', err);
            return true;
        } finally {
            setVerificandoEmail(false);
        }
    };

    const handleEmailBlur = async () => {
        if (!formData.email.trim()) return;
        const exists = await verificarEmailJaCadastrado(formData.email);
        setEmailEmUso(exists);
        if (exists) {
            setError('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
        }
    };

    const validateForm = () => {
        if (!formData.nome.trim()) {
            setError('Nome é obrigatório');
            return false;
        }
        if (!formData.email.trim()) {
            setError('Email é obrigatório');
            return false;
        }
        const emailValido = /\S+@\S+\.\S+/.test(formData.email.trim());
        if (!emailValido) {
            setError('Informe um e-mail válido');
            return false;
        }
        if (!formData.telefone.trim()) {
            setError('Telefone é obrigatório');
            return false;
        }
        if (!formData.senha) {
            setError('Senha é obrigatória');
            return false;
        }
        if (formData.senha.length < 6) {
            setError('Senha deve ter pelo menos 6 caracteres');
            return false;
        }
        if (formData.senha !== formData.confirmarSenha) {
            setError('Senhas não coincidem');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const emailJaExiste = await verificarEmailJaCadastrado(formData.email);
            if (emailJaExiste) {
                setEmailEmUso(true);
                setError('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
                return;
            }

            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: formData.email.trim().toLowerCase(),
                password: formData.senha,
                options: {
                    data: {
                        nome: formData.nome,
                        telefone: formData.telefone,
                        empresa: formData.empresa,
                        cargo: formData.cargo
                    }
                }
            });

            if (authError) {
                if (authError.message?.includes('already') || authError.message?.includes('exists')) {
                    setEmailEmUso(true);
                    throw new Error('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
                }
                throw authError;
            }

            if (!authData.user) {
                throw new Error('Erro ao criar usuário');
            }

            if (Array.isArray(authData.user.identities) && authData.user.identities.length === 0) {
                setEmailEmUso(true);
                throw new Error('Este e-mail já está cadastrado. Faça login ou recupere sua senha.');
            }

            const { data: existingCorretor, error: checkError } = await supabase
                .from('corretores')
                .select('id, email, created_at')
                .eq('user_id', authData.user.id)
                .single();

            if (existingCorretor) {
                setEmailEmUso(true);
                throw new Error('Este e-mail já está cadastrado. Faça login ou recupere sua senha.');
            }

            if (!existingCorretor && checkError?.code === 'PGRST116') {
                const { error: corretorError } = await supabase
                    .from('corretores')
                    .insert({
                        id: authData.user.id,
                        user_id: authData.user.id,
                        nome: formData.nome,
                        email: formData.email.trim().toLowerCase(),
                        telefone: formData.telefone
                    });

                if (corretorError) {
                    if (corretorError.code === '23505' && corretorError.message?.includes('email')) {
                        setEmailEmUso(true);
                        throw new Error('Este e-mail já está cadastrado. Faça login ou use outro e-mail.');
                    }
                    throw corretorError;
                }
            }

            if (afiliadoInfo) {
                const { error: indicacaoError } = await supabase
                    .from('indicacoes_afiliados')
                    .insert({
                        afiliado_id: afiliadoInfo.id,
                        email_indicado: formData.email,
                        nome_indicado: formData.nome,
                        telefone_indicado: formData.telefone,
                        corretor_indicado_id: authData.user.id,
                        status: 'concluida',
                        data_conversao: new Date().toISOString(),
                        valor_comissao: 0.00,
                        observacoes: `Cadastro realizado através do link de afiliado ${afiliadoInfo.codigo_afiliado}`
                    });

                if (indicacaoError) {
                    console.error('Erro ao criar indicação:', indicacaoError);
                }
            }

            setSuccess(true);
            setFormData({
                nome: '',
                email: '',
                telefone: '',
                senha: '',
                confirmarSenha: '',
                empresa: '',
                cargo: ''
            });

        } catch (err: unknown) {
            console.error('Erro no cadastro:', err);
            setError(err instanceof Error ? err.message : 'Erro ao realizar cadastro');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen relative overflow-hidden" style={{
                background: 'linear-gradient(135deg, #1E4F7A 10%, #E96B3B 60%, #1E4F7A 100%)'
            }}>
                {/* Background Effects */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 -left-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" style={{ backgroundColor: '#1E4F7A' }}></div>
                    <div className="absolute top-0 -right-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" style={{ backgroundColor: '#E96B3B' }}></div>
                    <div className="absolute -bottom-8 left-20 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" style={{ backgroundColor: '#1E4F7A' }}></div>
                </div>

                {/* Grid Pattern */}
                <div className="absolute inset-0">
                    <div className="w-full h-full opacity-10" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                        backgroundSize: '60px 60px'
                    }}></div>
                </div>

                <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 max-w-md w-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Cadastro Realizado!</h1>
                            <p className="text-gray-600 mb-6">
                                Seu cadastro foi realizado com sucesso. Verifique seu email para confirmar a conta.
                            </p>
                            <button
                                onClick={() => window.location.href = '/login'}
                                className="w-full text-white py-4 px-6 rounded-2xl font-semibold transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                                style={{
                                    background: 'linear-gradient(135deg, #1E4F7A 0%, #E96B3B 100%)',
                                    boxShadow: '0 4px 15px rgba(30, 79, 122, 0.3)'
                                }}
                            >
                                Ir para Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #1E4F7A 10%, #E96B3B 60%, #1E4F7A 100%)'
        }}>
            {/* Background Effects */}
            <div className="absolute inset-0">
                <div className="absolute top-0 -left-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob" style={{ backgroundColor: '#1E4F7A' }}></div>
                <div className="absolute top-0 -right-4 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000" style={{ backgroundColor: '#E96B3B' }}></div>
                <div className="absolute -bottom-8 left-20 w-72 h-72 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000" style={{ backgroundColor: '#1E4F7A' }}></div>
            </div>

            {/* Grid Pattern */}
            <div className="absolute inset-0">
                <div className="w-full h-full opacity-10" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '60px 60px'
                }}></div>
            </div>

            <div className="relative z-10 min-h-screen flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    {/* Logo Centralizada */}
                    <div className="text-center mb-8">
                        <img
                            src="/logo-fundo-escuro.png"
                            alt="Latera"
                            className="h-28 w-auto mx-auto mb-6"
                        />
                    </div>

                    {/* Form Card */}
                    <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">✨ Criar Conta</h2>
                            <p className="text-gray-600">
                                Preencha os dados para começar
                            </p>
                        </div>

                        {/* Informações do Afiliado */}
                        {afiliadoInfo && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <UserCheck className="w-5 h-5 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-900">Indicado por:</span>
                                </div>
                                <p className="text-blue-800 font-medium">{afiliadoInfo.corretor_nome}</p>
                                <p className="text-blue-600 text-sm">{afiliadoInfo.corretor_email}</p>
                            </div>
                        )}

                        {error && (
                            <div className="mb-6 p-4 rounded-xl border bg-red-50 border-red-200 text-red-700">
                                <div className="flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5" />
                                    <p className="text-sm font-medium">{error}</p>
                                </div>
                            </div>
                        )}

                        {/* Formulário */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Nome */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Nome Completo
                                </label>
                                <div className="relative group">
                                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                    <input
                                        type="text"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleInputChange}
                                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                                        placeholder="Seu nome completo"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Email */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Email
                                </label>
                                <div className="relative group">
                                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        onBlur={handleEmailBlur}
                                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                                        placeholder="seu@email.com"
                                        required
                                    />
                                </div>
                                {verificandoEmail && (
                                    <p className="text-xs text-blue-600">Verificando e-mail...</p>
                                )}
                                {!verificandoEmail && emailEmUso && (
                                    <p className="text-xs text-red-600">Este e-mail já está em uso.</p>
                                )}
                            </div>

                            {/* Telefone */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Telefone
                                </label>
                                <div className="relative group">
                                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                    <input
                                        type="tel"
                                        name="telefone"
                                        value={formData.telefone}
                                        onChange={handleInputChange}
                                        className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                                        placeholder="(85)9.9999.9999"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Senha */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Senha
                                </label>
                                <div className="relative group">
                                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        name="senha"
                                        value={formData.senha}
                                        onChange={handleInputChange}
                                        className="w-full pl-12 pr-14 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                                        placeholder="Mínimo 6 caracteres"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            {/* Confirmar Senha */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Confirmar Senha
                                </label>
                                <div className="relative group">
                                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        name="confirmarSenha"
                                        value={formData.confirmarSenha}
                                        onChange={handleInputChange}
                                        className="w-full pl-12 pr-14 py-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50 focus:bg-white"
                                        placeholder="Confirme sua senha"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="group w-full text-white py-4 px-6 rounded-2xl font-semibold focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                                style={{
                                    background: 'linear-gradient(135deg, #1E4F7A 0%, #E96B3B 100%)',
                                    boxShadow: '0 4px 15px rgba(30, 79, 122, 0.3)'
                                }}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                        Criando conta...
                                    </div>
                                ) : (
                                    'Criar Conta'
                                )}
                            </button>
                        </form>

                        <div className="mt-8 text-center">
                            <button
                                onClick={() => window.location.href = '/login'}
                                className="text-sm text-gray-600 font-medium transition-colors hover:text-[#1E4F7A]"
                            >
                                Já tem uma conta?{' '}
                                <span className="font-semibold text-[#1E4F7A]">
                                    Faça login
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="text-center mt-8">
                        <p className="text-sm text-white/70">
                            © 2025 Latera - Todos os Direitos Reservados
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cadastro;
